import { useEffect } from 'react';

import { MarketingHeader, MarketingFooter } from '../components/MarketingChrome';
import { ResourceSidebar } from '../components/ResourceSidebar';
import { appPath } from '../lib/config';
import '../components/landing.css';
import './getting-started.css';

const PAGE_TITLE = 'Use the fastest recorded lap reference | Paddiq';

export function IdealLineGuidePage() {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = PAGE_TITLE;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  return (
    <div className="landing marketing-home mh-interior">
      <a className="mh-skip" href="#main">
        Skip to content
      </a>
      <MarketingHeader />

      <main id="main">
        <section className="guide-hero">
          <div className="landing-container guide-hero__inner">
            <p className="section-kicker">Session analysis guide</p>
            <h1>Use the driver&apos;s fastest recorded lap as a reference.</h1>
            <p className="guide-hero__lead">
              Add the quickest valid historical lap for the same driver and exact circuit
              configuration to the map, then compare today&apos;s line with a lap the driver
              actually completed.
            </p>
            <div className="guide-hero__actions">
              <a
                className="landing-btn landing-btn--primary landing-btn--lg"
                href={appPath('/sessions')}
              >
                Open Sessions
              </a>
              <a
                className="landing-btn landing-btn--ghost landing-btn--lg"
                href="/resources/compare-laps-and-traces"
              >
                Compare laps and traces
              </a>
            </div>
            <p className="guide-hero__meta">Web guide · Reviewed October 1, 2026</p>
          </div>
        </section>

        <section className="guide-section">
          <div className="landing-container guide-layout">
            <aside className="guide-summary" aria-label="Guide summary">
              <ResourceSidebar currentPath="/resources/fastest-recorded-lap" />
              <p className="guide-summary__label">In this guide</p>
              <ol>
                <li>
                  <a href="#reference">Understand the reference</a>
                </li>
                <li>
                  <a href="#show">Show it on the map</a>
                </li>
                <li>
                  <a href="#compare">Compare responsibly</a>
                </li>
                <li>
                  <a href="#unavailable">Resolve an unavailable reference</a>
                </li>
              </ol>
              <div className="guide-summary__note">
                <strong>It is a real lap</strong>
                <span>
                  The reference comes from one completed valid lap. It is not a theoretical lap
                  assembled from different sector times.
                </span>
              </div>
            </aside>

            <article className="guide-content">
              <section className="guide-step" id="reference">
                <div className="guide-step__number" aria-hidden="true">
                  1
                </div>
                <div>
                  <p className="guide-step__eyebrow">Reference rules</p>
                  <h2>Match the driver and exact circuit configuration.</h2>
                  <p>
                    Paddiq looks across eligible sessions and selects the driver&apos;s fastest
                    complete valid lap for the same saved circuit configuration and revision. A lap
                    from another driver, a different configuration revision, or an incomplete
                    recording is not substituted.
                  </p>
                  <div className="guide-callout">
                    The status below the map records the lap number, lap time, recorded date,
                    configuration, and source session so the reference stays auditable.
                  </div>
                </div>
              </section>

              <section className="guide-step" id="show">
                <div className="guide-step__number" aria-hidden="true">
                  2
                </div>
                <div>
                  <p className="guide-step__eyebrow">Map overlay</p>
                  <h2>Show the fastest recorded lap.</h2>
                  <ol className="guide-instructions">
                    <li>
                      Open <strong>Sessions</strong> and choose the session and primary lap you want
                      to review.
                    </li>
                    <li>Wait for the map and session context to finish loading.</li>
                    <li>
                      In the telemetry map controls, turn on <strong>Fastest recorded lap</strong>.
                      The comparison workspace shows the same reference when it is available.
                    </li>
                    <li>
                      Use the dashed blue line and its status text to distinguish it from the
                      selected lap and other comparisons.
                    </li>
                  </ol>
                </div>
              </section>

              <section className="guide-step" id="compare">
                <div className="guide-step__number" aria-hidden="true">
                  3
                </div>
                <div>
                  <p className="guide-step__eyebrow">Coaching context</p>
                  <h2>Use the line to ask where the lap changed.</h2>
                  <ul className="guide-instructions">
                    <li>
                      Start with one section of track rather than judging the whole lap at once.
                    </li>
                    <li>
                      Use traces, turn speeds, braking, and coasting estimates for supporting
                      context.
                    </li>
                    <li>
                      Check the reference date and vehicle/session context before attributing a
                      difference to driving alone.
                    </li>
                    <li>
                      Treat the line as historical evidence, not a promise that it is safe or
                      repeatable under different conditions.
                    </li>
                  </ul>
                </div>
              </section>

              <section className="guide-troubleshooting" id="unavailable">
                <p className="guide-step__eyebrow">Troubleshooting</p>
                <h2>If the fastest recorded lap is unavailable</h2>
                <div className="guide-troubleshooting__grid">
                  <article>
                    <h3>Confirm the driver</h3>
                    <p>
                      Assign the intended driver to the session. The reference never borrows another
                      driver&apos;s lap.
                    </p>
                  </article>
                  <article>
                    <h3>Confirm the circuit</h3>
                    <p>
                      Assign the exact saved configuration. Similar venue names, layouts, or
                      directions do not replace a complete configuration identity match.
                    </p>
                  </article>
                  <article>
                    <h3>Build valid circuit laps</h3>
                    <p>
                      Verify the start/finish geometry, regenerate the circuit laps, and save a
                      complete valid result.
                    </p>
                  </article>
                  <article>
                    <h3>Check access and loading</h3>
                    <p>
                      Keep the session online while the reference loads. A historical session
                      contributes only when it is eligible for the current view.
                    </p>
                  </article>
                </div>
                <p>
                  If a matching configuration or accessible valid historical lap is missing, Paddiq
                  keeps the reference unavailable. It does not substitute a different driver’s lap
                  or a different layout.
                </p>
                <p>
                  The map will say whether the reference is loading, could not be loaded, or is
                  unavailable for the current driver and exact circuit configuration.
                </p>
              </section>

              <section className="guide-cta">
                <div>
                  <p className="guide-step__eyebrow">Compare with context</p>
                  <h2>Start with the fastest lap the driver actually completed.</h2>
                  <p>
                    Add traces and one representative comparison to understand how the current lap
                    differs from that historical reference.
                  </p>
                </div>
                <div className="guide-cta__actions">
                  <a
                    className="landing-btn landing-btn--primary landing-btn--lg"
                    href={appPath('/sessions')}
                  >
                    Open Sessions
                  </a>
                  <a className="landing-btn landing-btn--ghost landing-btn--lg" href="/support">
                    Contact support
                  </a>
                </div>
              </section>
            </article>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
}
